/**
 * Shopify Section Rendering helper
 * Fetches and updates specific sections without full page reload using Shopify's section rendering API
 */

import { globalState } from './utils'

interface SectionConfig {
  sectionId: string
  /** URL to render the section against. Sections in JSON templates only include
   * their settings/blocks when rendered on a page whose template contains them. */
  baseUrl?: string
  /** Extra section ids to try (in order) when `sectionId` isn't present in the response. */
  sectionIdFallbacks?: string[]
  fetchSelectors: string[]
  targetSelectors: string[]
  onSectionLoad?: (elements: HTMLElement[]) => void
}

interface SectionResponse {
  [key: string]: string
}

export class ShopifySectionHelper {
  private static async fetchSectionHtml(sectionIds: string | string[], baseUrl?: string): Promise<SectionResponse> {
    try {
      const sectionParam = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds
      const response = await fetch(`${baseUrl || globalState.routes.root}?sections=${sectionParam}`)
      if (!response.ok) throw new Error(`Failed to fetch sections: ${sectionParam}`)
      return await response.json()
    } catch (error) {
      console.error(`Error fetching sections ${sectionIds}:`, error)
      throw error
    }
  }

  private static createDOMFromString(htmlString: string): DocumentFragment {
    const template = document.createElement('template')
    template.innerHTML = htmlString.trim()
    return template.content
  }

  /**
   * Fetches a section by ID and updates the DOM based on provided selectors
   * @param config Configuration object for fetching and updating sections
   * @param sectionResponse Optional pre-fetched section response
   * @returns Promise<HTMLElement[]> Array of elements that were fetched from the section
   */
  static async fetchAndUpdateSection(
    config: SectionConfig,
    sectionResponse?: SectionResponse
  ): Promise<HTMLElement[]> {
    const { sectionId, baseUrl, sectionIdFallbacks = [], fetchSelectors, targetSelectors, onSectionLoad } = config

    if (fetchSelectors.length !== targetSelectors.length) {
      throw new Error('The number of fetch selectors must match the number of target selectors')
    }

    const sectionIds = [sectionId, ...sectionIdFallbacks]

    try {
      // Use provided section response or fetch if not provided
      const response = sectionResponse || await this.fetchSectionHtml(sectionIds, baseUrl)
      const resolvedSectionId = sectionIds.find((id) => response[id])
      const sectionHtml = resolvedSectionId ? response[resolvedSectionId] : null
      if (!sectionHtml) {
        throw new Error(`Section ${sectionIds.join(', ')} not found`)
      }
      const sectionFragment = this.createDOMFromString(sectionHtml)
      const fetchedElements: HTMLElement[] = []

      // Process each selector pair
      fetchSelectors.forEach((fetchSelector, index) => {
        const targetSelector = targetSelectors[index]
        // Find the element to fetch from the section
        const elementToFetch = sectionFragment.querySelector(fetchSelector) as HTMLElement
        if (!elementToFetch) {
          console.warn(`Could not find element with selector "${fetchSelector}" in section ${sectionId}`)
          return
        }

        // Find the target element to replace
        const targetElement = document.querySelector(targetSelector) as HTMLElement
        if (!targetElement) {
          console.warn(`Could not find target element with selector "${targetSelector}"`)
          return
        }

        // Replace the target element with the fetched element.
        //
        // Collect the INSERTED clone, not `elementToFetch`. The latter stays inside the
        // inert parsed fragment, so it is detached from the document and its custom
        // elements were never upgraded. Pushing it meant both the return value and the
        // `onSectionLoad` callback handed callers nodes they could not usefully act on —
        // any attempt to wire up the new DOM through them would silently no-op. No
        // current caller uses either, so this is a latent trap rather than a live bug.
        const insertedElement = elementToFetch.cloneNode(true) as HTMLElement
        targetElement.replaceWith(insertedElement)
        fetchedElements.push(insertedElement)
      })

      // Call the onSectionLoad callback if provided
      if (onSectionLoad) {
        onSectionLoad(fetchedElements)
      }

      return fetchedElements
    } catch (error) {
      console.error('Error in fetchAndUpdateSection:', error)
      throw error
    }
  }

  /**
   * Fetch multiple sections and update the DOM with a single request
   * @param configs Array of section configurations
   * @returns Promise<Map<string, HTMLElement[]>>
   */
  static async fetchAndUpdateMultipleSections(configs: SectionConfig[]): Promise<Map<string, HTMLElement[]>> {
    const results = new Map<string, HTMLElement[]>()

    // Get all unique section IDs (including fallbacks)
    const sectionIds = [...new Set(configs.flatMap(config => [config.sectionId, ...(config.sectionIdFallbacks || [])]))]

    // Fetch all sections in a single request
    const sectionsResponse = await this.fetchSectionHtml(sectionIds, configs[0]?.baseUrl)

    // Process each config with the pre-fetched response
    await Promise.all(
      configs.map(async (config) => {
        const elements = await this.fetchAndUpdateSection(config, sectionsResponse)
        results.set(config.sectionId, elements)
      })
    )

    return results
  }
}
